(function () {
  const configElement = document.getElementById("jt-phone-blocker-config");
  if (window.__jtPhoneBlockerLoaded) return;

  window.__jtPhoneBlockerLoaded = true;

  const proxyPath =
    configElement?.dataset.proxyPath ||
    "/apps/jt-phone-blocker/blocklist-check";
  const otpEnabled =
    configElement?.dataset.otpEnabled === "true" ||
    configElement?.dataset.otpEnabled === "1";
  const otpStartPath =
    configElement?.dataset.otpStartPath || "/apps/jt-phone-blocker/otp-start";
  const otpVerifyPath =
    configElement?.dataset.otpVerifyPath || "/apps/jt-phone-blocker/otp-verify";
  const blockMessage =
    configElement?.dataset.blockMessage ||
    "Something went wrong. Please contact support.";
  const phoneLabel = configElement?.dataset.phoneLabel || "Mobile number";
  const verifiedPhoneStorageKey = "jt-phone-blocker:verified-phone";
  const verifiedPhoneTimestampStorageKey = "jt-phone-blocker:verified-at";
  const verifiedOtpTokenStorageKey = "jt-phone-blocker:otp-token";
  const verifiedPhoneTtlMs = 24 * 60 * 60 * 1_000;
  const configuredResendCooldownSeconds = Number(
    configElement?.dataset.resendCooldownSeconds || "90",
  );
  const resendCooldownSeconds =
    Number.isFinite(configuredResendCooldownSeconds) &&
    configuredResendCooldownSeconds > 0
      ? Math.ceil(configuredResendCooldownSeconds)
      : 90;

  const checkoutSelectors = [
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    "#checkout",
    "button#checkout",
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button[formaction*="/checkout"]',
    'input[formaction*="/checkout"]',
    ".cart__checkout",
    ".cart__checkout-button",
    ".cart-drawer-checkout",
    ".cart-checkout-button",
    ".codk-cart-checkout",
    "#CartDrawer-Checkout",
    "#cart-sidebar-checkout",
    ".button-checkout",
    ".shopify-payment-button",
    ".shopify-payment-button__button",
    "shopify-accelerated-checkout",
    "shopify-accelerated-checkout-cart",
  ].join(",");

  let activePrompt = null;
  let suppressCheckoutClickUntil = 0;

  function findCheckoutTrigger(target) {
    return target instanceof Element ? target.closest(checkoutSelectors) : null;
  }

  function isCheckoutForm(form, submitter) {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute("action") || "";
    const submitterAction =
      submitter instanceof HTMLElement
        ? submitter.getAttribute("formaction") || ""
        : "";

    return (
      /\/cart(?:\?|$)/.test(action) ||
      /\/checkout(?:\?|$)/.test(action) ||
      /\/checkout(?:\?|$)/.test(submitterAction) ||
      Boolean(form.querySelector('[name="checkout"]'))
    );
  }

  function isCodKingCheckoutRedirect(event) {
    return event?.data?.type === "CODK_REDIRECT_TO_CHECKOUT";
  }

  function normalizePhone(phone) {
    return phone.replace(/[^\d+]/g, "").trim();
  }

  function canonicalPhone(phone) {
    const normalized = normalizePhone(phone);
    if (normalized.startsWith("+63")) return `63${normalized.slice(3)}`;
    if (normalized.startsWith("63")) return normalized;
    if (normalized.startsWith("0") && normalized.length === 11) {
      return `63${normalized.slice(1)}`;
    }
    return normalized;
  }

  function getStoredPhone() {
    try {
      return window.localStorage.getItem(verifiedPhoneStorageKey) || "";
    } catch {
      return "";
    }
  }

  function getStoredOtpToken() {
    try {
      return window.localStorage.getItem(verifiedOtpTokenStorageKey) || "";
    } catch {
      return "";
    }
  }

  function isStoredOtpTokenFresh(token) {
    if (!token) return false;

    const parts = String(token).split(".");
    if (parts.length !== 4) return false;

    const expiresAtMs = Number(parts[1]);
    return Number.isFinite(expiresAtMs) && Date.now() < expiresAtMs;
  }

  function setStoredPhone(phone, otpToken) {
    try {
      window.localStorage.setItem(verifiedPhoneStorageKey, phone);
      window.localStorage.setItem(
        verifiedPhoneTimestampStorageKey,
        String(Date.now()),
      );
      if (otpToken) {
        window.localStorage.setItem(verifiedOtpTokenStorageKey, otpToken);
      } else {
        window.localStorage.removeItem(verifiedOtpTokenStorageKey);
      }
    } catch {}
  }

  function hasFreshVerifiedPhone(phone) {
    const storedPhone = getStoredPhone();
    if (!storedPhone) return false;
    if (phone && canonicalPhone(storedPhone) !== canonicalPhone(phone)) {
      return false;
    }
    if (otpEnabled && !isStoredOtpTokenFresh(getStoredOtpToken())) {
      return false;
    }

    try {
      const verifiedAt = Number(
        window.localStorage.getItem(verifiedPhoneTimestampStorageKey) || "0",
      );
      return verifiedAt > 0 && Date.now() - verifiedAt < verifiedPhoneTtlMs;
    } catch {
      return false;
    }
  }

  function isFreshVerifiedPhone(phone) {
    return hasFreshVerifiedPhone(phone);
  }

  function isUsableButtonBackground(value) {
    if (!value) return false;

    const normalized = value.replace(/\s+/g, "").toLowerCase();
    return ![
      "transparent",
      "rgba(0,0,0,0)",
      "rgb(255,255,255)",
      "#fff",
      "#ffffff",
      "white",
    ].includes(normalized);
  }

  function normalizeThemeColor(value) {
    const trimmed = value.trim();
    if (/^\d+(?:\s*,\s*\d+){2}$/.test(trimmed)) {
      return `rgb(${trimmed})`;
    }
    return trimmed;
  }

  function getThemeButtonBackground() {
    const rootStyles = window.getComputedStyle(document.documentElement);
    const rootVariables = [
      "--color-button",
      "--buttons-primary-background-color",
      "--button-background-color",
      "--button-background",
      "--color-accent",
    ];

    for (const variableName of rootVariables) {
      const color = normalizeThemeColor(
        rootStyles.getPropertyValue(variableName),
      );
      if (isUsableButtonBackground(color)) return color;
    }

    const buttonSelectors = [
      'button[name="add"]',
      ".product-form__submit",
      ".cart__checkout-button",
      ".shopify-payment-button__button",
      ".button",
      "button[type='submit']",
    ].join(",");

    for (const button of Array.from(
      document.querySelectorAll(buttonSelectors),
    )) {
      const color = window.getComputedStyle(button).backgroundColor;
      if (isUsableButtonBackground(color)) return color;
    }

    return "#111827";
  }

  function showVerifiedNotice() {
    const existingNotice = document.getElementById(
      "jt-phone-blocker-verified-notice",
    );
    if (existingNotice) existingNotice.remove();

    const notice = document.createElement("div");
    notice.id = "jt-phone-blocker-verified-notice";
    notice.setAttribute("role", "status");
    notice.textContent =
      "Mobile number verified. You can continue to checkout.";
    notice.style.cssText =
      "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;max-width:min(92vw,420px);border-radius:999px;background:#111827;color:#fff;box-shadow:0 12px 36px rgba(15,23,42,.28);padding:12px 18px;font:650 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;text-align:center;";

    document.body.append(notice);
    window.setTimeout(() => {
      notice.remove();
    }, 5_000);
  }

  function navigateToFreshCheckout() {
    window.location.assign("/checkout");
  }

  async function continueToCheckoutAfterGuard(guardPromise) {
    try {
      const allowed = await guardPromise;
      if (allowed) navigateToFreshCheckout();
    } catch {
      showBlockNotice("We could not verify this number. Please try again.");
    }
  }

  function showBlockNotice(message) {
    const existingNotice = document.getElementById(
      "jt-phone-blocker-error-notice",
    );
    if (existingNotice) existingNotice.remove();

    const notice = document.createElement("div");
    notice.id = "jt-phone-blocker-error-notice";
    notice.setAttribute("role", "alert");
    notice.textContent = message || blockMessage;
    notice.style.cssText =
      "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;max-width:min(92vw,420px);border-radius:999px;background:#991b1b;color:#fff;box-shadow:0 12px 36px rgba(15,23,42,.28);padding:12px 18px;font:650 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;text-align:center;";

    document.body.append(notice);
    window.setTimeout(() => {
      notice.remove();
    }, 5_000);
  }

  function formatCountdown(seconds) {
    const safeSeconds = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = String(safeSeconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  }

  async function getCartDetails() {
    try {
      const response = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return {};

      const cart = await response.json();
      return {
        token: typeof cart.token === "string" ? cart.token : undefined,
        itemCount:
          typeof cart.item_count === "number" ? cart.item_count : undefined,
      };
    } catch {
      return {};
    }
  }

  function resolveEndpointUrl(path) {
    return new URL(path, window.location.origin);
  }

  function isCrossOrigin(url) {
    return url.origin !== window.location.origin;
  }

  async function postEndpointJson(path, payload) {
    const endpointUrl = resolveEndpointUrl(path);
    const crossOrigin = isCrossOrigin(endpointUrl);
    const response = await fetch(endpointUrl.toString(), {
      method: "POST",
      credentials: crossOrigin ? "omit" : "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": crossOrigin
          ? "text/plain;charset=UTF-8"
          : "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return { ok: false };
    }

    return response.json();
  }

  async function postSameOriginJson(path, payload) {
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return { ok: false };
    }

    return response.json();
  }

  async function startOtp(phone) {
    const cart = await getCartDetails();
    return postEndpointJson(otpStartPath, {
      phone,
      shopDomain: window.location.hostname,
      cartToken: cart.token,
      cartTotalQuantity: cart.itemCount,
    });
  }

  async function verifyOtp(challengeId, phone, code) {
    return postEndpointJson(otpVerifyPath, {
      challengeId,
      phone,
      code,
    });
  }

  async function writeOtpCartAttribute(token) {
    if (!token) return false;

    const response = await postSameOriginJson("/cart/update.js", {
      attributes: {
        jt_phone_otp_token: token,
      },
    });
    if (response.ok === false) return false;

    return true;
  }

  async function reuseStoredOtpVerification(phone) {
    const storedPhone = getStoredPhone();
    const storedOtpToken = getStoredOtpToken();
    if (!storedPhone || !isStoredOtpTokenFresh(storedOtpToken)) {
      return { ok: false, blocked: false };
    }
    if (phone && canonicalPhone(storedPhone) !== canonicalPhone(phone)) {
      return { ok: false, blocked: false };
    }

    const phoneToCheck = phone || storedPhone;
    const check = await checkPhone(phoneToCheck);
    if (check.blocked) {
      return {
        ok: true,
        blocked: true,
        message: check.message || blockMessage,
      };
    }
    if (!check.ok) return { ok: false, blocked: false };

    const cartAttributeWritten = await writeOtpCartAttribute(storedOtpToken);
    if (!cartAttributeWritten) return { ok: false, blocked: false };

    setStoredPhone(phoneToCheck, storedOtpToken);
    return { ok: true, blocked: false };
  }

  function renderPrompt(prefillPhone) {
    if (activePrompt) return activePrompt;

    activePrompt = new Promise((resolve) => {
      let otpChallengeId = null;
      let currentPhone = "";
      let resendCooldownTimer = null;

      const overlay = document.createElement("div");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;padding:20px;";

      const panel = document.createElement("div");
      panel.style.cssText =
        "width:min(100%,420px);border-radius:18px;background:#fff;color:#111827;box-shadow:0 24px 80px rgba(15,23,42,.28);padding:24px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;";

      const title = document.createElement("h2");
      title.textContent = "Confirm mobile number";
      title.style.cssText =
        "font-size:22px;line-height:1.2;margin:0 0 8px;font-weight:750;";

      const copy = document.createElement("p");
      copy.textContent =
        "Verify your mobile number to continue. We’ll send a one-time code. Once verified, you won’t need another code on this device for 24 hours.";
      copy.style.cssText =
        "font-size:14px;line-height:1.5;margin:0 0 18px;color:#6b7280;";

      const label = document.createElement("label");
      label.textContent = phoneLabel;
      label.style.cssText =
        "display:block;font-size:13px;font-weight:650;margin-bottom:8px;";

      const input = document.createElement("input");
      input.type = "tel";
      input.autocomplete = "tel";
      input.inputMode = "tel";
      input.placeholder = "09XXXXXXXXX";
      input.value = prefillPhone || getStoredPhone();
      input.style.cssText =
        "width:100%;box-sizing:border-box;border:1px solid #d1d5db;border-radius:10px;padding:12px 14px;font-size:16px;outline:none;";

      const codeLabel = document.createElement("label");
      codeLabel.textContent = "Enter the verification code";
      codeLabel.style.cssText =
        "display:none;font-size:13px;font-weight:650;margin:16px 0 8px;";

      const codeInput = document.createElement("input");
      codeInput.type = "text";
      codeInput.autocomplete = "one-time-code";
      codeInput.inputMode = "numeric";
      codeInput.placeholder = "123456";
      codeInput.maxLength = 8;
      codeInput.style.cssText =
        "display:none;width:100%;box-sizing:border-box;border:1px solid #d1d5db;border-radius:10px;padding:12px 14px;font-size:16px;outline:none;letter-spacing:.08em;";

      const error = document.createElement("p");
      error.style.cssText =
        "min-height:20px;margin:10px 0 0;color:#dc2626;font-size:13px;";

      const actions = document.createElement("div");
      actions.style.cssText =
        "display:flex;gap:10px;justify-content:flex-end;margin-top:22px;";

      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.textContent = "Cancel";
      cancel.style.cssText =
        "border:1px solid #d1d5db;background:#fff;color:#111827;border-radius:10px;padding:11px 16px;font-weight:650;cursor:pointer;";

      const continueButton = document.createElement("button");
      continueButton.type = "button";
      continueButton.textContent = otpEnabled ? "Send code" : "Continue";
      continueButton.style.cssText =
        "border:0;color:#fff;border-radius:10px;padding:11px 18px;font-weight:750;cursor:pointer;";
      continueButton.style.background = getThemeButtonBackground();

      const resendButton = document.createElement("button");
      resendButton.type = "button";
      resendButton.textContent = "Resend code";
      resendButton.style.cssText =
        "display:none;align-items:center;border:0;background:transparent;color:#374151;border-radius:10px;padding:11px 8px;font-weight:650;cursor:pointer;text-decoration:underline;";

      function stopResendCooldown() {
        if (resendCooldownTimer) {
          window.clearInterval(resendCooldownTimer);
          resendCooldownTimer = null;
        }
      }

      function resetResendButton() {
        stopResendCooldown();
        resendButton.disabled = false;
        resendButton.textContent = "Resend code";
      }

      function startResendCooldown() {
        stopResendCooldown();

        let remainingSeconds = resendCooldownSeconds;
        resendButton.disabled = true;

        const tick = () => {
          if (remainingSeconds <= 0) {
            resetResendButton();
            return;
          }

          resendButton.textContent = `Resend code in ${formatCountdown(
            remainingSeconds,
          )}`;
          remainingSeconds -= 1;
        };

        tick();
        resendCooldownTimer = window.setInterval(tick, 1_000);
      }

      function close(value) {
        stopResendCooldown();
        overlay.remove();
        activePrompt = null;
        resolve(value);
      }

      cancel.addEventListener("click", () => close(null));
      continueButton.addEventListener("click", async () => {
        const phone = normalizePhone(input.value);
        if (!phone) {
          error.textContent = "Enter a mobile number to continue.";
          input.focus();
          return;
        }

        continueButton.disabled = true;
        error.textContent = "";

        if (otpEnabled && !otpChallengeId) {
          continueButton.textContent = "Sending...";

          if (isFreshVerifiedPhone(phone)) {
            const storedVerification = await reuseStoredOtpVerification(phone);
            continueButton.disabled = false;
            continueButton.textContent = "Send code";

            if (storedVerification.blocked) {
              error.textContent = storedVerification.message || blockMessage;
              return;
            }
            if (storedVerification.ok) {
              close(phone);
              return;
            }
            continueButton.disabled = true;
            continueButton.textContent = "Sending...";
          }

          const otp = await startOtp(phone);
          continueButton.disabled = false;
          continueButton.textContent = "Verify code";

          if (otp.blocked) {
            error.textContent = otp.message || blockMessage;
            continueButton.textContent = "Send code";
            return;
          }

          if (otp.enabled === false) {
            const check = await checkPhone(phone);
            if (check.blocked) {
              error.textContent = check.message || blockMessage;
              continueButton.textContent = "Send code";
              return;
            }
            if (!check.ok) {
              error.textContent =
                "We could not verify this number. Please try again.";
              continueButton.textContent = "Send code";
              return;
            }
            setStoredPhone(phone);
            close(phone);
            return;
          }

          if (!otp.ok || !otp.challengeId) {
            error.textContent =
              otp.message || "We could not send a code. Please try again.";
            continueButton.textContent = "Send code";
            return;
          }

          otpChallengeId = otp.challengeId;
          currentPhone = phone;
          input.disabled = true;
          codeLabel.style.display = "block";
          codeInput.style.display = "block";
          resendButton.style.display = "inline-flex";
          startResendCooldown();
          copy.textContent =
            "Enter the 6-digit code sent to your mobile number. The code expires in 10 minutes.";
          if (otp.debugOtp) {
            error.textContent = `Test code: ${otp.debugOtp}`;
          }
          codeInput.focus();
          return;
        }

        if (otpEnabled && otpChallengeId) {
          continueButton.textContent = "Verifying...";
          const code = codeInput.value.replace(/\D/g, "");
          if (!code) {
            continueButton.disabled = false;
            continueButton.textContent = "Verify code";
            error.textContent = "Enter the verification code to continue.";
            codeInput.focus();
            return;
          }

          const verification = await verifyOtp(
            otpChallengeId,
            currentPhone || phone,
            code,
          );
          continueButton.disabled = false;
          continueButton.textContent = "Verify code";

          if (!verification.ok || !verification.verified) {
            error.textContent =
              verification.message || "Invalid or expired code.";
            return;
          }

          const cartAttributeWritten = await writeOtpCartAttribute(
            verification.token,
          );
          if (!cartAttributeWritten) {
            error.textContent =
              "We could not verify this number. Please try again.";
            return;
          }
          setStoredPhone(currentPhone || phone, verification.token);
          close(currentPhone || phone);
          return;
        }

        continueButton.textContent = "Checking...";

        const check = await checkPhone(phone);
        continueButton.disabled = false;
        continueButton.textContent = "Continue";

        if (check.blocked) {
          error.textContent = check.message || blockMessage;
          return;
        }

        if (!check.ok) {
          error.textContent =
            "We could not verify this number. Please try again.";
          return;
        }

        setStoredPhone(phone);
        close(phone);
      });

      resendButton.addEventListener("click", async () => {
        if (!otpEnabled || !otpChallengeId || !currentPhone) return;

        resendButton.disabled = true;
        continueButton.disabled = true;
        resendButton.textContent = "Resending...";
        error.textContent = "";

        const otp = await startOtp(currentPhone);

        continueButton.disabled = false;

        if (otp.blocked) {
          resetResendButton();
          error.textContent = otp.message || blockMessage;
          return;
        }

        if (!otp.ok || !otp.challengeId) {
          resetResendButton();
          error.textContent =
            otp.message || "We could not send a code. Please try again.";
          return;
        }

        otpChallengeId = otp.challengeId;
        codeInput.value = "";
        if (otp.debugOtp) {
          error.textContent = `Test code: ${otp.debugOtp}`;
        }
        startResendCooldown();
        codeInput.focus();
      });

      input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          continueButton.click();
        }
      });

      codeInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          continueButton.click();
        }
      });

      actions.append(cancel, resendButton, continueButton);
      panel.append(
        title,
        copy,
        label,
        input,
        codeLabel,
        codeInput,
        error,
        actions,
      );
      overlay.append(panel);
      document.body.append(overlay);
      input.focus();
    });

    return activePrompt;
  }

  async function checkPhone(phone) {
    try {
      const endpointUrl = resolveEndpointUrl(proxyPath);
      endpointUrl.searchParams.set("phone", phone);
      endpointUrl.searchParams.set("shopDomain", window.location.hostname);
      const response = await fetch(endpointUrl.toString(), {
        credentials: isCrossOrigin(endpointUrl) ? "omit" : "same-origin",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        return { ok: false, blocked: false, message: null };
      }

      return response.json();
    } catch {
      return { ok: false, blocked: false, message: null };
    }
  }

  async function guardCheckout(prefillPhone) {
    const phone = await renderPrompt(prefillPhone);
    if (!phone) return false;

    showVerifiedNotice();
    return true;
  }

  async function guardStoredCheckout(prefillPhone, onBlocked) {
    const storedVerification = await reuseStoredOtpVerification(prefillPhone);
    if (storedVerification.blocked) {
      if (typeof onBlocked === "function") {
        onBlocked(storedVerification.message || blockMessage);
      } else {
        showBlockNotice(storedVerification.message || blockMessage);
      }
      return false;
    }
    if (storedVerification.ok) {
      showVerifiedNotice();
      return true;
    }

    return guardCheckout(prefillPhone);
  }

  function isCodKingOtpSubmit(target) {
    const button = target instanceof Element ? target.closest("button") : null;
    if (!button) return null;

    const text = (button.textContent || "").replace(/\s+/g, " ").trim();
    if (text !== "Send Code") return null;

    return looksLikeCodKingOtpSubmit(button) ? button : null;
  }

  function looksLikeCodKingOtpSubmit(button) {
    const root =
      button.closest(
        ".codking-otp-login,.codk-otp-widget-box,[class*='codk'],[class*='codking']",
      ) || document;
    const modalText = root.textContent || "";

    return Boolean(
      document.querySelector('input[name="phone"]') ||
      document.querySelector('input[placeholder="1 (702) 123-4567"]') ||
      root.querySelector('input[name="phone"]') ||
      root.querySelector('input[type="tel"]') ||
      modalText.includes("COD King") ||
      modalText.includes("Verify Mobile Number"),
    );
  }

  function findCodKingPhoneInput(trigger) {
    const root =
      trigger.closest(
        ".codking-otp-login,.codk-otp-widget-box,[class*='codk'],[class*='codking']",
      ) || document;

    return (
      root.querySelector('input[name="phone"]') ||
      root.querySelector('input[type="tel"]') ||
      root.querySelector('input[placeholder="1 (702) 123-4567"]') ||
      Array.from(root.querySelectorAll("input")).find((input) =>
        /^\+?63|\d{10,12}/.test(input.value || ""),
      ) ||
      null
    );
  }

  function showCodKingBlockError(trigger, message) {
    const root =
      trigger.closest(
        ".codking-otp-login,.codk-otp-widget-box,[class*='codk'],[class*='codking']",
      ) ||
      trigger.parentElement ||
      document.body;
    const existing = root.querySelector("#jt-phone-blocker-codking-error");
    const error = existing || document.createElement("p");

    error.id = "jt-phone-blocker-codking-error";
    error.setAttribute("role", "alert");
    error.textContent = message || blockMessage;
    error.style.cssText =
      "margin:10px 0 0;color:#dc2626;font:500 13px/1.45 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;";

    if (!existing) {
      const input = findCodKingPhoneInput(trigger);
      const anchor = input?.closest("label,div") || input || trigger;
      anchor.insertAdjacentElement("afterend", error);
    }
  }

  async function guardCodKingOtpSubmit(trigger) {
    const input = findCodKingPhoneInput(trigger);
    const phone = normalizePhone(input?.value || "");
    if (!phone) return;

    if (otpEnabled) {
      continueToCheckoutAfterGuard(guardCheckout(phone));
      return;
    }

    const previousText = trigger.textContent;
    trigger.disabled = true;
    trigger.textContent = "Checking...";

    const check = await checkPhone(phone);

    trigger.disabled = false;
    trigger.textContent = previousText || "Send Code";

    if (check.blocked) {
      showCodKingBlockError(trigger, check.message || blockMessage);
      return;
    }

    if (!check.ok) {
      showCodKingBlockError(
        trigger,
        "We could not verify this number. Please try again.",
      );
      return;
    }

    setStoredPhone(phone);
    showVerifiedNotice();
  }

  function guardCheckoutTrigger(prefillPhone) {
    if (hasFreshVerifiedPhone(prefillPhone)) {
      continueToCheckoutAfterGuard(guardStoredCheckout(prefillPhone));
      return;
    }

    continueToCheckoutAfterGuard(guardCheckout(prefillPhone));
  }

  function interceptCheckoutEvent(event, trigger) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.type !== "click") {
      suppressCheckoutClickUntil = Date.now() + 1_500;
    }

    guardCheckoutTrigger();
  }

  function handleCheckoutEarlyIntent(event) {
    const trigger = findCheckoutTrigger(event.target);
    if (!trigger) return;

    interceptCheckoutEvent(event, trigger);
  }

  function handleCheckoutClick(event) {
    const codKingOtpSubmit = isCodKingOtpSubmit(event.target);
    if (codKingOtpSubmit) {
      const codKingPhone = findCodKingPhoneInput(codKingOtpSubmit);
      const phone = normalizePhone(codKingPhone?.value || "");
      if (!phone) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (isFreshVerifiedPhone(phone)) {
        continueToCheckoutAfterGuard(
          guardStoredCheckout(phone, (message) =>
            showCodKingBlockError(codKingOtpSubmit, message),
          ),
        );
        return;
      }

      guardCodKingOtpSubmit(codKingOtpSubmit);
      return;
    }

    const trigger = findCheckoutTrigger(event.target);
    if (!trigger) return;

    if (Date.now() < suppressCheckoutClickUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    interceptCheckoutEvent(event, trigger);
  }

  function handleCheckoutSubmit(event) {
    const submitEvent = event;
    const form = submitEvent.target;
    const submitter = submitEvent.submitter;
    if (!isCheckoutForm(form, submitter)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (hasFreshVerifiedPhone()) {
      continueToCheckoutAfterGuard(guardStoredCheckout());
      return;
    }

    continueToCheckoutAfterGuard(guardCheckout());
  }

  window.addEventListener("pointerdown", handleCheckoutEarlyIntent, true);
  document.addEventListener("pointerdown", handleCheckoutEarlyIntent, true);
  window.addEventListener("mousedown", handleCheckoutEarlyIntent, true);
  document.addEventListener("mousedown", handleCheckoutEarlyIntent, true);
  window.addEventListener("touchstart", handleCheckoutEarlyIntent, true);
  document.addEventListener("touchstart", handleCheckoutEarlyIntent, true);
  window.addEventListener("click", handleCheckoutClick, true);
  document.addEventListener("click", handleCheckoutClick, true);

  window.addEventListener(
    "message",
    (event) => {
      if (!isCodKingCheckoutRedirect(event)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (hasFreshVerifiedPhone()) {
        continueToCheckoutAfterGuard(guardStoredCheckout());
        return;
      }

      continueToCheckoutAfterGuard(guardCheckout());
    },
    true,
  );

  window.addEventListener("submit", handleCheckoutSubmit, true);
  document.addEventListener("submit", handleCheckoutSubmit, true);
})();
